// apps/frontend/admin-ui/src/pages/reports-provider.ts
// Provider-level Reports section: one parameterized page over three dimensions —
// By integrator, By game, By player — with drill-down between them. Native
// currency: money metrics are per (dimension, currency) and never summed across
// currencies, so rows are grouped by currency with a per-currency subtotal.
// apx holds zero balance and takes no revenue share, so there are no
// platform/upline/downline settlement columns — just the game-provider economics.
//
// Drill graph:
//   By integrator → click a row → By game filtered to that integrator.
//   By game       → click a row → By integrator filtered to that game.
//   By player     → click a row → that player's detail page.
import {
  AdminApiError,
  type AdminClient,
  type IntegratorSummary,
} from '@apx/admin-sdk';
import { exportXlsxWorkbook } from '../xlsx.js';
import { fmtMoney, fmtNumber } from '../format.js';
import { toDecimalString } from '@apx/money';
import { emptyState } from '../empty-state.js';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';

import { wireDatePickers } from '../date-picker.js';

export type ProviderReportView = 'by-integrator' | 'by-game' | 'by-player';

export interface ProviderReportDeps {
  client: AdminClient;
  view: ProviderReportView;
  /** Integrators for name/slug resolution in the drill breadcrumb. */
  integrators: IntegratorSummary[];
  /** Active drill filters, read from the query string. */
  drillIntegratorId?: string;
  drillGameId?: string;
  /**
   * Rendered inside one integrator's console rather than the provider one.
   * Drops the cross-tenant chrome: the by-integrator tab is a league table of
   * other tenants, and the by-game row drill leads straight into it.
   */
  scopedToIntegrator?: boolean;
}

const VIEW_META: Record<ProviderReportView, { title: string; sub: string }> = {
  'by-integrator': {
    title: 'Report · by integrator',
    sub: 'Betting economics per integrator, split by currency. Click a row to drill into that integrator’s games.',
  },
  'by-game': {
    title: 'Report · by game',
    sub: 'Betting economics per apx game, split by currency, across all integrators. Click a row to see the integrators driving that game.',
  },
  'by-player': {
    title: 'Report · by player',
    sub: 'Top players by turnover, split by currency. Click a row to open the player’s detail. Capped at the top 200.',
  },
};

/**
 * Subtitles for the single-integrator rendering. The provider copy promises
 * cross-tenant reach ("across all integrators", "click a row to see the
 * integrators driving that game") that is neither true nor offered here.
 */
const SCOPED_SUB: Partial<Record<ProviderReportView, string>> = {
  'by-game': 'Betting economics per game for this integrator, split by currency.',
  'by-player':
    'This integrator’s top players by turnover, split by currency. Click a row to open the player’s detail. Capped at the top 200.',
};

/** Shared row shape the table renderer consumes — each view maps its rows to this. */
interface Row {
  href: string;
  label: string; // primary cell (integrator/game/player)
  sublabel?: string; // secondary line under the label (slug / integrator)
  currency: string;
  players?: number;
  betCount: number;
  turnover: string;
  payout: string;
  primary: string; // GGR (house view) or Net (player view)
  primaryLabel: 'GGR' | 'Net';
  rtp: number;
  jpContribute?: string;
}

export async function renderProviderReportPage(root: HTMLElement, deps: ProviderReportDeps): Promise<void> {
  const meta = VIEW_META[deps.view];
  const sub = deps.scopedToIntegrator ? (SCOPED_SUB[deps.view] ?? meta.sub) : meta.sub;
  const currencies = await loadCurrencies(deps.client);
  let currency = '';
  let lastRows: Row[] = [];
  let lastExport: Array<Record<string, string | number>> = [];
  // Filters the last run used. The export carries them so a saved workbook
  // records which window and currency produced its numbers.
  let lastMeta: { since?: string; from?: string; to?: string; currency: string } | null = null;

  const currencyOptions = ['<option value="">All currencies</option>']
    .concat(currencies.map((c) => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`))
    .join('');

  const cols = deps.view === 'by-player' ? 7 : 9;

  root.innerHTML = `
    <h1 class="page-title">${escapeHtml(meta.title)}</h1>
    <p class="page-sub">${escapeHtml(sub)}</p>
    ${renderTabs(deps)}
    ${renderBreadcrumb(deps)}

    <div class="card report-filters">
      <div class="filter-row">
        <label class="filter-field">Currency
          <select id="pr-currency" class="field-input">${currencyOptions}</select>
        </label>
        <label class="filter-field">Window
          <select id="pr-since" class="field-input">
            <option value="24h" selected>Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom range…</option>
          </select>
        </label>
        <span id="pr-custom" class="filter-field-group hidden">
          <span class="filter-field"><label for="pr-from">From</label><input id="pr-from" type="datetime-local" step="1" class="field-input" /></span>
          <span class="filter-field"><label for="pr-to">To</label><input id="pr-to" type="datetime-local" step="1" class="field-input" /></span>
        </span>
        <button id="pr-run" class="primary">Refresh</button>
        <button id="pr-export" class="ghost">Export Excel</button>
        <span id="pr-msg" class="muted filter-msg"></span>
      </div>
    </div>

    <div id="pr-summary" style="margin:2px 0 14px"></div>
    <div class="card"><div id="pr-body">${skeletonTable({ rows: 6, columns: cols })}</div></div>
  `;

  const bodyEl = root.querySelector<HTMLElement>('#pr-body')!;
  const summaryEl = root.querySelector<HTMLElement>('#pr-summary')!;
  const msgEl = root.querySelector<HTMLElement>('#pr-msg')!;
  const sinceSel = root.querySelector<HTMLSelectElement>('#pr-since')!;
  const customEl = root.querySelector<HTMLElement>('#pr-custom')!;
  const fromEl = root.querySelector<HTMLInputElement>('#pr-from')!;
  const toEl = root.querySelector<HTMLInputElement>('#pr-to')!;

  function readWindow(): { since?: string; from?: string; to?: string; error?: string } {
    if (sinceSel.value === 'custom') {
      if (!fromEl.value || !toEl.value) return { error: 'Pick both From and To for a custom range.' };
      const from = new Date(fromEl.value);
      const to = new Date(toEl.value);
      if (from >= to) return { error: 'From must be before To.' };
      return { from: from.toISOString(), to: to.toISOString() };
    }
    return { since: sinceSel.value };
  }

  async function run(): Promise<void> {
    const w = readWindow();
    if (w.error) {
      msgEl.textContent = w.error;
      msgEl.className = 'error filter-msg';
      return;
    }
    msgEl.textContent = 'Running…';
    msgEl.className = 'muted filter-msg';
    bodyEl.innerHTML = skeletonTable({ rows: 6, columns: cols });
    summaryEl.innerHTML = '';
    const win = { ...(w.since ? { since: w.since } : {}), ...(w.from ? { from: w.from } : {}), ...(w.to ? { to: w.to } : {}) };
    const ccy = currency ? { currency } : {};
    lastMeta = { ...win, currency };
    try {
      if (deps.view === 'by-integrator') {
        const rep = await deps.client.fetchProviderIntegratorReport({ ...win, ...ccy, ...(deps.drillGameId ? { gameId: deps.drillGameId } : {}) });
        lastRows = rep.rows.map((r) => ({
          href: `/reports-by-game?integratorId=${encodeURIComponent(r.integratorId)}`,
          label: r.name,
          sublabel: r.slug,
          currency: r.currency,
          players: r.players,
          betCount: r.betCount,
          turnover: r.turnover,
          payout: r.payout,
          primary: r.ggr,
          primaryLabel: 'GGR',
          rtp: r.rtp,
          jpContribute: r.jpContribute,
        }));
        lastExport = rep.rows.map((r) => ({ Integrator: r.name, Slug: r.slug, Currency: r.currency, Players: r.players, Bets: r.betCount, Turnover: dec(r.turnover, r.currency), Payout: dec(r.payout, r.currency), GGR: dec(r.ggr, r.currency), RTP: r.rtp.toFixed(6), JackpotContribute: dec(r.jpContribute, r.currency) }));
      } else if (deps.view === 'by-game') {
        const rep = await deps.client.fetchProviderGameReport({ ...win, ...ccy, ...(deps.drillIntegratorId ? { integratorId: deps.drillIntegratorId } : {}) });
        lastRows = rep.rows.map((r) => ({
          // Scoped: the row drill leads to the cross-tenant league table, so it
          // becomes a within-tenant player breakdown for that game instead.
          href: deps.scopedToIntegrator
            ? `/integrators/${encodeURIComponent(deps.drillIntegratorId ?? '')}/reports/by-player?gameId=${encodeURIComponent(r.gameId)}`
            : `/reports-by-integrator?gameId=${encodeURIComponent(r.gameId)}`,
          label: r.gameId,
          currency: r.currency,
          players: r.players,
          betCount: r.betCount,
          turnover: r.turnover,
          payout: r.payout,
          primary: r.ggr,
          primaryLabel: 'GGR',
          rtp: r.rtp,
          jpContribute: r.jpContribute,
        }));
        lastExport = rep.rows.map((r) => ({ Game: r.gameId, Currency: r.currency, Players: r.players, Bets: r.betCount, Turnover: dec(r.turnover, r.currency), Payout: dec(r.payout, r.currency), GGR: dec(r.ggr, r.currency), RTP: r.rtp.toFixed(6), JackpotContribute: dec(r.jpContribute, r.currency) }));
      } else {
        const rep = await deps.client.fetchProviderPlayerReport({ ...win, ...ccy, ...(deps.drillIntegratorId ? { integratorId: deps.drillIntegratorId } : {}), ...(deps.drillGameId ? { gameId: deps.drillGameId } : {}) });
        lastRows = rep.rows.map((r) => ({
          href: `/integrators/${encodeURIComponent(r.integratorId)}/players/${encodeURIComponent(r.playerId)}`,
          label: r.displayName,
          sublabel: r.integratorPlayerRef,
          currency: r.currency,
          betCount: r.betCount,
          turnover: r.turnover,
          payout: r.payout,
          primary: r.net,
          primaryLabel: 'Net',
          rtp: r.rtp,
        }));
        lastExport = rep.rows.map((r) => ({ Player: r.displayName, Identity: r.integratorPlayerRef, Integrator: r.integratorId, Currency: r.currency, Bets: r.betCount, Turnover: dec(r.turnover, r.currency), Payout: dec(r.payout, r.currency), Net: dec(r.net, r.currency), RTP: r.rtp.toFixed(6) }));
      }
      summaryEl.innerHTML = renderSummary(lastRows, deps.view);
      bodyEl.innerHTML = renderTable(lastRows, deps.view);
      msgEl.textContent = '';
    } catch (err) {
      const m = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.textContent = m;
      msgEl.className = 'error filter-msg';
      bodyEl.innerHTML = `<p class="error">${escapeHtml(m)}</p>`;
    }
  }

  sinceSel.addEventListener('change', () => {
    customEl.classList.toggle('hidden', sinceSel.value !== 'custom');
    if (sinceSel.value !== 'custom') void run();
  });
  wireDatePickers(root);
  root.querySelector<HTMLSelectElement>('#pr-currency')!.addEventListener('change', (e) => {
    currency = (e.target as HTMLSelectElement).value;
    void run();
  });
  root.querySelector<HTMLButtonElement>('#pr-run')!.addEventListener('click', () => void run());
  root.querySelector<HTMLButtonElement>('#pr-export')!.addEventListener('click', () => {
    if (lastExport.length === 0) {
      showToast('Run the report first — nothing to export.', 'warn');
      return;
    }
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const m = lastMeta;
    const overview: Array<Record<string, string | number>> = [
      { Metric: 'Report', Value: VIEW_META[deps.view].title },
      { Metric: 'Window', Value: m?.from && m?.to ? 'Custom range' : (m?.since ?? '') },
      { Metric: 'From', Value: m?.from ?? '' },
      { Metric: 'To', Value: m?.to ?? '' },
      { Metric: 'Currency', Value: m?.currency ? m.currency : 'All currencies' },
      { Metric: 'Generated at', Value: new Date().toISOString() },
      { Metric: 'Rows', Value: lastExport.length },
    ];
    exportXlsxWorkbook(`apx-report-${deps.view}-${stamp}.xlsx`, [
      { name: 'Overview', rows: overview, columns: ['Metric', 'Value'] },
      { name: 'Data', rows: lastExport },
    ]);
    showToast(`Exported ${lastExport.length} rows.`, 'success');
  });

  await run();
}

function renderTabs(deps: ProviderReportDeps): string {
  const scoped = deps.scopedToIntegrator === true;
  const tab = (view: ProviderReportView, label: string): string => {
    const active = view === deps.view;
    // Switching tab drops any active drill filter — start fresh at the list.
    // Scoped rendering stays inside the integrator's own URL space so the tab
    // switch keeps the tenant rather than escaping to the provider console.
    const href = scoped
      ? `/integrators/${encodeURIComponent(deps.drillIntegratorId ?? '')}/reports/${view}`
      : `/reports-${view}`;
    return `<a href="${href}" data-route="reports-${view}" class="rtp-subtab${active ? ' active' : ''}">${label}</a>`;
  };
  return `<div class="rtp-subtabs jp-seg" style="margin:4px 0 14px">
    ${scoped ? '' : tab('by-integrator', 'By integrator')}
    ${tab('by-game', 'By game')}
    ${tab('by-player', 'By player')}
  </div>`;
}

function renderBreadcrumb(deps: ProviderReportDeps): string {
  const scoped = deps.scopedToIntegrator === true;
  const chips: string[] = [];
  // Scoped: naming the integrator restates the console you are already in, and
  // it is not a filter you could clear anyway.
  if (deps.drillIntegratorId && !scoped) {
    const name = deps.integrators.find((i) => i.id === deps.drillIntegratorId)?.name ?? deps.drillIntegratorId.slice(0, 8);
    chips.push(`<span class="pill">Integrator: ${escapeHtml(name)}</span>`);
  }
  if (deps.drillGameId) {
    chips.push(`<span class="pill">Game: ${escapeHtml(deps.drillGameId)}</span>`);
  }
  if (chips.length === 0) return '';
  const base = scoped
    ? `/integrators/${encodeURIComponent(deps.drillIntegratorId ?? '')}/reports/${deps.view}`
    : `/reports-${deps.view}`;
  return `<p class="muted" style="margin:0 0 12px;display:flex;align-items:center;gap:8px;flex-wrap:wrap">
    Filtered by ${chips.join(' ')}
    <a href="${base}" class="row-link">✕ clear</a>
  </p>`;
}

/**
 * Rows grouped under a currency SECTION HEADER (native model — amounts across
 * currencies aren't comparable, so we separate them visually and drop the
 * redundant per-row currency column). A subtotal row is shown only when a
 * currency actually has more than one row — otherwise it would just duplicate
 * the single data row.
 */
function renderTable(rows: Row[], view: ProviderReportView): string {
  if (rows.length === 0) {
    return emptyState({ icon: 'list', title: 'No activity in this window', hint: 'Widen the window or clear the currency filter.' });
  }
  const showPlayers = view !== 'by-player';
  const showJp = view !== 'by-player';
  const primaryHdr = view === 'by-player' ? 'Net' : 'GGR';
  const firstHdr = view === 'by-integrator' ? 'Integrator' : view === 'by-game' ? 'Game' : 'Player';
  // #, first, [Players], Bets, Turnover, Payout, primary, RTP, [JP]
  const colCount = 6 + (showPlayers ? 1 : 0) + (showJp ? 1 : 0);

  const groups = new Map<string, Row[]>();
  for (const r of rows) {
    const g = groups.get(r.currency) ?? [];
    g.push(r);
    groups.set(r.currency, g);
  }

  let n = 0;
  const groupHtml = [...groups.entries()]
    .map(([ccy, grp]) => {
      const header = `<tr class="ccy-section"><td colspan="${colCount}" style="padding:14px 0 6px;border-bottom:1px solid var(--border,#2a2f3a)"><span class="pill">${escapeHtml(ccy)}</span> <span class="muted" style="font-size:11px">${grp.length} ${firstHdr.toLowerCase()}${grp.length === 1 ? '' : 's'}</span></td></tr>`;

      const dataRows = grp
        .map((r) => {
          n += 1;
          const primClass = BigInt(r.primary) >= 0n ? 'pos' : 'neg';
          return `<tr class="row-nav" data-row-href="${r.href}" title="Drill in">
            <td class="num">${n}</td>
            <td><strong>${escapeHtml(r.label)}</strong>${r.sublabel ? `<div class="muted" style="font-size:11px"><code>${escapeHtml(r.sublabel)}</code></div>` : ''}</td>
            ${showPlayers ? `<td class="num">${(r.players ?? 0).toLocaleString()}</td>` : ''}
            <td class="num">${fmtNumber(r.betCount.toString())}</td>
            <td class="num">${fmtMoney(r.turnover, ccy)}</td>
            <td class="num">${fmtMoney(r.payout, ccy)}</td>
            <td class="num ${primClass}">${fmtMoney(r.primary, ccy)}</td>
            <td class="num">${pct(r.rtp)}</td>
            ${showJp ? `<td class="num">${fmtMoney(r.jpContribute ?? '0', ccy)}</td>` : ''}
          </tr>`;
        })
        .join('');

      // Subtotal only earns its keep when the currency has >1 row.
      if (grp.length < 2) return header + dataRows;
      const sub = grp.reduce(
        (a, r) => ({
          players: a.players + (r.players ?? 0),
          bets: a.bets + r.betCount,
          turnover: a.turnover + BigInt(r.turnover),
          payout: a.payout + BigInt(r.payout),
          primary: a.primary + BigInt(r.primary),
          jp: a.jp + BigInt(r.jpContribute ?? '0'),
        }),
        { players: 0, bets: 0, turnover: 0n, payout: 0n, primary: 0n, jp: 0n },
      );
      const subRtp = sub.turnover > 0n ? Number(sub.payout) / Number(sub.turnover) : 0;
      const subRow = `<tr class="data-total">
        <td></td>
        <td>Subtotal · ${escapeHtml(ccy)}</td>
        ${showPlayers ? `<td class="num">${sub.players.toLocaleString()}</td>` : ''}
        <td class="num">${fmtNumber(sub.bets.toString())}</td>
        <td class="num">${fmtMoney(sub.turnover, ccy)}</td>
        <td class="num">${fmtMoney(sub.payout, ccy)}</td>
        <td class="num ${sub.primary >= 0n ? 'pos' : 'neg'}">${fmtMoney(sub.primary, ccy)}</td>
        <td class="num">${pct(subRtp)}</td>
        ${showJp ? `<td class="num">${fmtMoney(sub.jp, ccy)}</td>` : ''}
      </tr>`;
      return header + dataRows + subRow;
    })
    .join('');

  return `<div class="data-table-wrap"><table class="data-table">
    <thead><tr>
      <th class="num">#</th>
      <th>${firstHdr}</th>
      ${showPlayers ? '<th class="num">Players</th>' : ''}
      <th class="num">Bets</th>
      <th class="num">Turnover</th>
      <th class="num">Payout</th>
      <th class="num">${primaryHdr}</th>
      <th class="num">RTP</th>
      ${showJp ? '<th class="num">JP contribute</th>' : ''}
    </tr></thead>
    <tbody>${groupHtml}</tbody>
  </table></div>`;
}

/** Headline KPI cards, one cluster per currency (never blended across
 *  currencies). Totals come from the same rows the table renders. */
function renderSummary(rows: Row[], view: ProviderReportView): string {
  if (rows.length === 0) return '';
  const entityLabel = view === 'by-integrator' ? 'integrators' : view === 'by-game' ? 'games' : 'players';

  const groups = new Map<string, Row[]>();
  for (const r of rows) {
    const g = groups.get(r.currency) ?? [];
    g.push(r);
    groups.set(r.currency, g);
  }

  return [...groups.entries()]
    .map(([ccy, grp]) => {
      const t = grp.reduce(
        (a, r) => ({
          turnover: a.turnover + BigInt(r.turnover),
          payout: a.payout + BigInt(r.payout),
          bets: a.bets + r.betCount,
          // by-player rows have no per-row player count — each row IS one player.
          players: a.players + (r.players ?? 1),
        }),
        { turnover: 0n, payout: 0n, bets: 0, players: 0 },
      );
      const ggr = t.turnover - t.payout;
      const rtp = t.turnover > 0n ? Number(t.payout) / Number(t.turnover) : 0;
      return `<div style="margin-bottom:10px">
        <div style="margin:0 0 6px"><span class="pill">${escapeHtml(ccy)}</span> <span class="muted" style="font-size:11px">${grp.length} ${entityLabel}</span></div>
        <div class="players-stats">
          ${card('Turnover', money(t.turnover.toString(), ccy))}
          ${card('GGR / house take', money(ggr.toString(), ccy), ggr >= 0n ? 'pos' : 'neg')}
          ${card('RTP', pct(rtp))}
          ${card('Players', t.players.toLocaleString())}
          ${card('Bets', fmtNumber(t.bets.toString()))}
        </div>
      </div>`;
    })
    .join('');
}

function card(label: string, value: string, valueClass = ''): string {
  return `<div class="stat-card"><div class="stat-card-label">${escapeHtml(label)}</div><div class="stat-card-value ${valueClass}">${value}</div></div>`;
}

/** A total in its currency, with the code: "1,234.56 USD", "169,000 IDR". */
function money(v: string, ccy: string): string {
  return `${fmtMoney(v, ccy)} ${escapeHtml(ccy)}`;
}

/** An export cell: the amount as a plain decimal in its currency ("1234.56"). */
function dec(v: string, ccy: string): string {
  return toDecimalString(v, ccy);
}

async function loadCurrencies(client: AdminClient): Promise<string[]> {
  try {
    const rows = await client.listCurrencies();
    return rows.filter((c) => c.enabled).sort((a, b) => a.sortOrder - b.sortOrder).map((c) => c.code);
  } catch {
    return [];
  }
}

function pct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}
